import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { usePollWhile } from '../common/useLiveReload';
import { LoadingRegion } from '../common/icons';
import { connectError, percent, runBadge, runLine } from './searchState';
import type { SearchConsoleProperty, SearchQueuePage, SearchReport } from '../../types';

/**
 * What search has made of this site, and what to do about the pages it ignores.
 *
 * THE RULE (backend: search_console/lifecycle.py). A page with no Search Console
 * impressions 28 days after publishing lands in "Retarget" — aim it at a nearby
 * keyword, or leave it. At 56 days still at zero it lands in "Retire" — noindex
 * and out of the sitemap, or back to draft on WordPress. Nothing is retired
 * without somebody pressing the button here.
 *
 * Self-fetching, matching `SiteReleases` and `SiteStructurePanel`.
 */
export default function SiteSearchPanel({ siteId }: { siteId: number }) {
  const [params, setParams] = useSearchParams();
  const [report, setReport] = useState<SearchReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  // AFTER A PULL OR A RETIRE, KEEP LOOKING. Both run in the background, and
  // the panel said "numbers appear here when it finishes" and then never
  // looked again. It re-reads every 10 seconds for three minutes.
  const [watchUntil, setWatchUntil] = useState(0);

  const load = useCallback(async () => {
    setError(null);
    try {
      setReport(await api.getSearchReport(siteId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load search data');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  // The OAuth callback lands here with ?gsc=connected or ?gsc_error=…; say so
  // once, then take the parameter off the URL so a reload doesn't repeat it.
  useEffect(() => {
    const ok = params.get('gsc');
    const err = params.get('gsc_error');
    if (!ok && !err) return;
    setFlash(err ? connectError(err) : 'Search Console connected.');
    const next = new URLSearchParams(params);
    next.delete('gsc');
    next.delete('gsc_error');
    setParams(next, { replace: true });
  }, [params, setParams]);

  usePollWhile(watchUntil > Date.now(), load, 10_000);

  const act = async (fn: () => Promise<string | void>) => {
    setBusy(true);
    setFlash(null);
    try {
      const message = await fn();
      if (message) setFlash(message);
      await load();
      setWatchUntil(Date.now() + 180_000);
    } catch (e) {
      setFlash(e instanceof Error ? e.message : 'That did not work');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <LoadingRegion label="Loading search data…" />;
  if (error || !report) {
    return <div className="text-sm text-red-600 dark:text-red-400">{error ?? 'No data'}</div>;
  }

  return (
    <div className="space-y-6">
      {flash && (
        <div className="px-3 py-2 text-sm rounded bg-blue-50 dark:bg-blue-900/30 text-blue-900 dark:text-blue-200">
          {flash}
        </div>
      )}

      <ConnectionCard siteId={siteId} report={report} busy={busy} act={act} />

      {report.connection.connected && report.connection.property_url && (
        <>
          <Kpis report={report} />
          <Runs report={report} />
          <RetargetQueue siteId={siteId} pages={report.retarget_queue} busy={busy} act={act} />
          <RetireQueue siteId={siteId} report={report} busy={busy} act={act} />
          {report.retired_pages.length > 0 && <RetiredList pages={report.retired_pages} />}
        </>
      )}
    </div>
  );
}

type Act = (fn: () => Promise<string | void>) => Promise<void>;

const card = 'p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md';
const h4 = 'text-sm font-semibold text-gray-900 dark:text-gray-100';
const muted = 'text-xs text-gray-500 dark:text-gray-400';
const btn =
  'px-3 py-1.5 text-xs font-medium rounded disabled:opacity-50 disabled:cursor-not-allowed';
const btnPrimary = `${btn} bg-purple-600 text-white hover:bg-purple-700`;
const btnGhost = `${btn} bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600`;
const btnDanger = `${btn} bg-red-600 text-white hover:bg-red-700`;

function ConnectionCard({
  siteId,
  report,
  busy,
  act,
}: {
  siteId: number;
  report: SearchReport;
  busy: boolean;
  act: Act;
}) {
  const conn = report.connection;
  const [properties, setProperties] = useState<SearchConsoleProperty[] | null>(null);
  const [choice, setChoice] = useState<string>('');

  const connect = () =>
    act(async () => {
      const { url } = await api.getSearchConsoleAuthUrl(siteId);
      window.location.href = url;
    });

  const loadProperties = () =>
    act(async () => {
      const res = await api.getSearchConsoleProperties(siteId);
      setProperties(res.properties);
      setChoice(conn.property_url ?? res.suggested ?? res.properties[0]?.site_url ?? '');
      if (!res.properties.length) {
        return 'This Google account cannot see any Search Console properties. Add the site in Search Console, or connect a different account.';
      }
    });

  if (!conn.connected) {
    return (
      <div className={card}>
        <h4 className={h4}>Google Search Console</h4>
        <p className={`${muted} mt-1 mb-3`}>
          Connect this site's Search Console property to see which pages Google is showing, and
          to find the ones it isn't. Uses read-only access. Add the site to Search Console first
          if you haven't - a Domain property covers every version of the address.
        </p>
        <button className={btnPrimary} disabled={busy} onClick={connect}>
          Connect Search Console
        </button>
      </div>
    );
  }

  const picking = properties !== null || !conn.property_url;
  return (
    <div className={card}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h4 className={h4}>Google Search Console</h4>
          <p className={`${muted} mt-1`}>
            {conn.property_url ? (
              <>
                Property <span className="font-mono">{conn.property_url}</span>
                {conn.synced_through ? ` · data through ${conn.synced_through}` : ' · first pull pending'}
              </>
            ) : (
              'Connected - choose which property is this site.'
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {conn.property_url && (
            <button
              className={btnGhost}
              disabled={busy}
              onClick={() =>
                act(async () => {
                  await api.syncSearchConsole(siteId);
                  return 'Pull queued. Numbers appear here when it finishes - usually within a minute or two.';
                })
              }
            >
              Pull now
            </button>
          )}
          {!picking && (
            <button className={btnGhost} disabled={busy} onClick={loadProperties}>
              Change property
            </button>
          )}
          <button
            className={btnGhost}
            disabled={busy}
            onClick={() => {
              if (!window.confirm('Disconnect Search Console? Stored numbers are kept, but pages stop being checked until you reconnect.')) return;
              act(async () => {
                await api.disconnectSearchConsole(siteId);
              });
            }}
          >
            Disconnect
          </button>
        </div>
      </div>

      {conn.auth_failed_at && (
        <div className="mt-3 px-3 py-2 text-sm rounded bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 flex items-center justify-between gap-3">
          <span>Google stopped accepting this connection. Reconnect to resume the nightly pull.</span>
          <button className={btnPrimary} disabled={busy} onClick={connect}>
            Reconnect
          </button>
        </div>
      )}

      {picking && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {properties === null ? (
            <button className={btnPrimary} disabled={busy} onClick={loadProperties}>
              Choose property
            </button>
          ) : (
            <>
              <select
                className="text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-2 py-1"
                value={choice}
                onChange={(e) => setChoice(e.target.value)}
              >
                {properties.map((p) => (
                  <option key={p.site_url} value={p.site_url}>
                    {p.site_url}
                  </option>
                ))}
              </select>
              <button
                className={btnPrimary}
                disabled={busy || !choice}
                onClick={() =>
                  act(async () => {
                    await api.setSearchConsoleProperty(siteId, choice);
                    setProperties(null);
                    return 'Property saved. The first pull reaches back 90 days.';
                  })
                }
              >
                Use this property
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Kpis({ report }: { report: SearchReport }) {
  const tiles: { label: string; value: string; hint: string }[] = [
    { label: 'Live pages', value: report.live.toLocaleString(), hint: 'Published and not retired.' },
    {
      label: 'Shown in search, 28 days',
      value: report.with_impressions_28d.toLocaleString(),
      hint: 'Pages with at least one impression in the last 28 days of data.',
    },
    {
      label: 'Impressions · clicks, 28 days',
      value: `${report.impressions_28d.toLocaleString()} · ${report.clicks_28d.toLocaleString()}`,
      hint: 'Totals across live pages.',
    },
    {
      label: 'Dead share',
      value: percent(report.dead_share),
      hint: 'Live pages at zero impressions after 28+ days. This is the ratio a spam classifier can see - keep it falling.',
    },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {tiles.map((t) => (
        <div key={t.label} className={card} title={t.hint}>
          <div className={muted}>{t.label}</div>
          <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100 tabular-nums">
            {t.value}
          </div>
        </div>
      ))}
    </div>
  );
}

function Runs({ report }: { report: SearchReport }) {
  if (!report.runs.length) {
    return <p className={muted}>No pulls yet. The first runs within the hour, or press Pull now.</p>;
  }
  return (
    <div className={card}>
      <h4 className={`${h4} mb-2`}>Last pulls</h4>
      <ul className="space-y-1.5">
        {report.runs.map((r) => {
          const badge = runBadge(r.status);
          return (
            <li key={r.id} className="flex items-baseline gap-2 text-sm">
              <span className={`px-1.5 py-0.5 text-xs rounded ${badge.className}`}>{badge.label}</span>
              <span className="text-gray-500 dark:text-gray-400 tabular-nums">
                {r.started_at ? new Date(r.started_at).toLocaleString() : ''}
              </span>
              <span className="text-gray-700 dark:text-gray-300">{runLine(r)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function useSelection(pages: SearchQueuePage[]) {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  // Drop ids that left the list after a reload.
  useEffect(() => {
    setSelected((prev) => new Set(Array.from(prev).filter((id) => pages.some((p) => p.id === id))));
  }, [pages]);
  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const all = pages.length > 0 && selected.size === pages.length;
  const toggleAll = () => setSelected(all ? new Set() : new Set(pages.map((p) => p.id)));
  return { selected, toggle, all, toggleAll, clear: () => setSelected(new Set()) };
}

function PageCell({ page }: { page: SearchQueuePage }) {
  return (
    <div className="min-w-0">
      <div className="text-sm text-gray-900 dark:text-gray-100 truncate">{page.title ?? `Page ${page.id}`}</div>
      <div className="text-xs text-gray-500 dark:text-gray-400 font-mono truncate">{page.path}</div>
    </div>
  );
}

function RetargetQueue({
  siteId,
  pages,
  busy,
  act,
}: {
  siteId: number;
  pages: SearchQueuePage[];
  busy: boolean;
  act: Act;
}) {
  const sel = useSelection(pages);
  const [picked, setPicked] = useState<Record<number, number>>({});

  return (
    <div className={card}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <div>
          <h4 className={h4}>Retarget · {pages.length}</h4>
          <p className={`${muted} mt-0.5 max-w-2xl`}>
            No impressions after 28 days. About half of these pick up on their own before day 56,
            so leaving one alone is a real option. Retarget only when a nearby keyword is worth
            it - ★ marks small, winnable ones. The page keeps its address; its content is rebuilt.
          </p>
        </div>
        {sel.selected.size > 0 && (
          <button
            className={btnGhost}
            disabled={busy}
            onClick={() =>
              act(async () => {
                const { kept } = await api.keepPages(siteId, Array.from(sel.selected));
                sel.clear();
                return `${kept} page(s) kept - they are out of the lifecycle now.`;
              })
            }
          >
            Keep {sel.selected.size} as is
          </button>
        )}
      </div>
      {pages.length === 0 ? (
        <p className={muted}>Nothing to retarget.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 dark:text-gray-400">
              <th className="w-8 py-1">
                <input type="checkbox" checked={sel.all} onChange={sel.toggleAll} aria-label="Select all" />
              </th>
              <th className="py-1">Page</th>
              <th className="py-1 w-20">Days live</th>
              <th className="py-1">Aim it at</th>
            </tr>
          </thead>
          <tbody>
            {pages.map((p) => {
              const choice = picked[p.id] ?? p.suggestions[0]?.topic_keyword_id;
              return (
                <tr key={p.id} className="border-t border-gray-100 dark:border-gray-700 align-top">
                  <td className="py-2">
                    <input type="checkbox" checked={sel.selected.has(p.id)} onChange={() => sel.toggle(p.id)} aria-label="Select page" />
                  </td>
                  <td className="py-2 pr-3">
                    <PageCell page={p} />
                    {p.keyword && <div className={muted}>now: {p.keyword}</div>}
                  </td>
                  <td className="py-2 tabular-nums text-gray-700 dark:text-gray-300">{p.days_live ?? '-'}</td>
                  <td className="py-2">
                    {p.suggestions.length === 0 ? (
                      <span className={muted}>No unused keyword nearby - likely one to let go.</span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <select
                          className="text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-2 py-1 max-w-xs"
                          value={choice}
                          onChange={(e) => setPicked((prev) => ({ ...prev, [p.id]: Number(e.target.value) }))}
                        >
                          {p.suggestions.map((s) => (
                            <option key={s.topic_keyword_id} value={s.topic_keyword_id}>
                              {s.winnable ? '★ ' : ''}
                              {s.keyword}
                              {s.avg_monthly_searches != null ? ` · ${s.avg_monthly_searches.toLocaleString()}/mo` : ''}
                            </option>
                          ))}
                        </select>
                        <button
                          className={btnPrimary}
                          disabled={busy || !choice}
                          onClick={() =>
                            act(async () => {
                              await api.retargetPage(siteId, p.id, choice!);
                              return 'Rebuild queued. Its 28 days start again when the new version deploys.';
                            })
                          }
                        >
                          Retarget
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function RetireQueue({
  siteId,
  report,
  busy,
  act,
}: {
  siteId: number;
  report: SearchReport;
  busy: boolean;
  act: Act;
}) {
  const pages = report.retire_queue;
  const sel = useSelection(pages);
  const blocked = !!report.retire_blocker;

  const retire = () => {
    const n = sel.selected.size;
    if (
      !window.confirm(
        `Retire ${n} page(s)?\n\n` +
          'Each one is redeployed as noindex and left out of the sitemap (or moved back to draft on WordPress), ' +
          'and pages linking to it are redeployed without that link - except any with unpublished ' +
          'changes, which are left alone so nothing unapproved goes live.\n\n' +
          'Deploys send every page already marked ready in the same plans, too.',
      )
    )
      return;
    act(async () => {
      const res = await api.retirePages(siteId, Array.from(sel.selected));
      sel.clear();
      const skipped = Object.keys(res.not_redeployed).length;
      return (
        `Retired ${res.retired.length}. Redeploying ${res.redeploying.length} page(s)` +
        (res.plans_busy.length ? `; ${res.plans_busy.length} plan(s) were mid-deploy and will send them next run` : '') +
        (skipped
          ? `. ${skipped} page(s) were left as they are, so nothing unpublished goes live: ` +
            // Distinct reasons only — twenty identical lines say less than one.
            Array.from(new Set(Object.values(res.not_redeployed))).join(' ')
          : '.')
      );
    });
  };

  return (
    <div className={card}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <div>
          <h4 className={h4}>Retire · {pages.length}</h4>
          <p className={`${muted} mt-0.5 max-w-2xl`}>
            Still no impressions after 56 days. Retiring takes a page out of search so the share of
            the site that is dead stops growing. You can keep any you want to hold on to.
          </p>
        </div>
        {sel.selected.size > 0 && (
          <div className="flex items-center gap-2">
            <button
              className={btnGhost}
              disabled={busy}
              onClick={() =>
                act(async () => {
                  const { kept } = await api.keepPages(siteId, Array.from(sel.selected));
                  sel.clear();
                  return `${kept} page(s) kept.`;
                })
              }
            >
              Keep {sel.selected.size}
            </button>
            <button className={btnDanger} disabled={busy || blocked} onClick={retire}>
              Retire {sel.selected.size}
            </button>
          </div>
        )}
      </div>
      {report.retire_blocker && (
        <div className="mb-2 px-3 py-2 text-sm rounded bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200">
          {report.retire_blocker}
        </div>
      )}
      {report.retire_notice && !report.retire_blocker && <p className={`${muted} mb-2`}>{report.retire_notice}</p>}
      {pages.length === 0 ? (
        <p className={muted}>Nothing to retire.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 dark:text-gray-400">
              <th className="w-8 py-1">
                <input type="checkbox" checked={sel.all} onChange={sel.toggleAll} aria-label="Select all" />
              </th>
              <th className="py-1">Page</th>
              <th className="py-1 w-20">Days live</th>
            </tr>
          </thead>
          <tbody>
            {pages.map((p) => (
              <tr key={p.id} className="border-t border-gray-100 dark:border-gray-700">
                <td className="py-2">
                  <input type="checkbox" checked={sel.selected.has(p.id)} onChange={() => sel.toggle(p.id)} aria-label="Select page" />
                </td>
                <td className="py-2 pr-3">
                  <PageCell page={p} />
                </td>
                <td className="py-2 tabular-nums text-gray-700 dark:text-gray-300">{p.days_live ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function RetiredList({ pages }: { pages: SearchQueuePage[] }) {
  return (
    <details className={card}>
      <summary className={`${h4} cursor-pointer`}>Retired · {pages.length}</summary>
      <ul className="mt-2 space-y-1">
        {pages.map((p) => (
          <li key={p.id} className="flex items-baseline justify-between gap-3">
            <PageCell page={p} />
            <span className={muted}>{p.retired_at ? new Date(p.retired_at).toLocaleDateString() : ''}</span>
          </li>
        ))}
      </ul>
    </details>
  );
}
